import type { Account } from '@fleece/models';
import { ChevronRight, Plus } from 'lucide-react';
import { useState } from 'react';
import { CreateAccountDialog } from '@/components/create-account-dialog';
import { AccountStatus, AccountTypeBadge, RefreshButton } from '@/components/ledger-parts';
import { Rows } from '@/components/settings/table-parts';
import { Button, PageHeader, Table, TableCard, Td, Th } from '@/components/ui';
import { useAccounts } from '@/hooks/use-ledger';
import { easternDate } from '@/lib/format';
import { accountHash } from '@/lib/route';
import { cn } from '@/lib/utils';

/**
 * Every virtual account, from one request. A row opens the account, where its positions
 * and profit are read — here they would cost two requests per row.
 */
export function AccountsPage() {
  const accounts = useAccounts();
  const list = accounts.data?.accounts ?? [];
  const [creating, setCreating] = useState(false);

  return (
    <div className="mx-auto flex max-w-[1100px] flex-col gap-3 px-2 py-3 pb-6 sm:px-3">
      <PageHeader title="Accounts" description="Each strategy trades under a virtual account of its own, so what it has made reads apart from the rest of the broker account." />

      <TableCard
        title="Virtual accounts"
        count={accounts.data === undefined ? undefined : list.length}
        action={
          <span className="flex items-center gap-1">
            <RefreshButton />
            <Button variant="primary" onClick={() => setCreating(true)}>
              <Plus className="size-3.5" /> Add account
            </Button>
          </span>
        }
      >
        <Rows query={accounts} count={list.length} empty="No virtual account yet. Add one for each strategy that trades.">
          <Table>
            <thead>
              <tr>
                <Th>Account</Th>
                <Th className="hidden sm:table-cell">Type</Th>
                <Th className="hidden sm:table-cell">Status</Th>
                <Th className="hidden md:table-cell">Created</Th>
                <Th>
                  <span className="sr-only">Open</span>
                </Th>
              </tr>
            </thead>
            <tbody>
              {list.map((account) => (
                <AccountRow key={account.accountId} account={account} />
              ))}
            </tbody>
          </Table>
        </Rows>
      </TableCard>

      <CreateAccountDialog open={creating} onClose={() => setCreating(false)} />
    </div>
  );
}

/**
 * The whole row opens the account, for a pointer; the name is the link, so the keyboard,
 * a screen reader and a middle-click each get a real one.
 */
function AccountRow({ account }: { account: Account }) {
  const href = accountHash(account.accountId);

  return (
    <tr
      onClick={() => {
        window.location.hash = href;
      }}
      className={cn('cursor-pointer transition-colors hover:bg-surface-2/60', account.status === 'inactive' && 'text-ink-2')}
    >
      <Td>
        <div className="flex min-w-0 flex-col gap-0.5">
          <a
            href={href}
            onClick={(event) => event.stopPropagation()}
            className="w-fit text-sm font-medium hover:underline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent"
          >
            {account.name}
          </a>
          <span className="font-mono text-[11px] text-muted">{account.accountId}</span>
          <span className="flex items-center gap-2 sm:hidden">
            <AccountTypeBadge type={account.accountType} />
            <AccountStatus status={account.status} />
          </span>
        </div>
      </Td>
      <Td className="hidden sm:table-cell">
        <AccountTypeBadge type={account.accountType} />
      </Td>
      <Td className="hidden sm:table-cell">
        <AccountStatus status={account.status} />
      </Td>
      <Td className="hidden text-ink-2 tabular md:table-cell">{easternDate(account.createdAt)}</Td>
      <Td className="w-8 pr-2 text-muted">
        <ChevronRight className="size-4" />
      </Td>
    </tr>
  );
}
